import React, { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

/**
 * FeedbackForm
 * Form for evaluators to submit or edit feedback on a speech.
 */
export default function FeedbackForm({
  onSubmit,
  initialContent = '',
  isEditing = false,
  onCancel,
  disabled = false,
}) {
  const [content, setContent] = useState(initialContent);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (content.trim().length < 10) {
      setError('Feedback must be at least 10 characters.');
      return;
    }

    if (content.trim().length > 5000) {
      setError('Feedback must be under 5000 characters.');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(content.trim());
      if (!isEditing) {
        setContent('');
      }
    } catch (err) {
      setError(err.message || 'Failed to submit feedback.');
    } finally {
      setLoading(false);
    }
  };

  const charCount = content.length;
  const isOverLimit = charCount > 5000;

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label htmlFor="feedback" className="block text-sm font-bold mb-1.5" style={{ color: 'var(--text-muted)' }}>
          {isEditing ? 'Edit your feedback' : 'Your feedback'}
        </label>
        <textarea
          id="feedback"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What worked? What would make this speaker better? Be specific. Be honest."
          rows={4}
          disabled={disabled || loading}
          className="w-full px-3 py-2 text-sm resize-none rounded-xl font-medium"
          style={{
            background: 'var(--bg-input)',
            border: `1px solid ${isOverLimit ? 'var(--color-danger-border)' : 'var(--border-accent)'}`,
            color: 'var(--text-primary)',
            outline: 'none',
            transition: 'border-color 0.15s ease',
          }}
          onFocus={e => { e.target.style.borderColor = isOverLimit ? 'var(--color-danger-border)' : 'var(--border-accent)'; }}
          onBlur={e => { e.target.style.borderColor = isOverLimit ? 'var(--color-danger-border)' : 'var(--border-accent)'; }}
        />
        <div className="flex justify-between mt-1">
          <p
            className="text-xs font-bold font-mono"
            style={{ color: isOverLimit ? 'var(--color-danger)' : 'var(--text-faint)' }}
          >
            {charCount}/5000
          </p>
          {charCount < 10 && charCount > 0 && (
            <p className="text-xs font-bold" style={{ color: 'var(--accent)' }}>
              {10 - charCount} more to go
            </p>
          )}
        </div>
      </div>

      {error && (
        <div
          className="px-3 py-2 rounded-xl text-sm font-medium"
          style={{ color: 'var(--color-danger)', background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
        >
          {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={disabled || loading || isOverLimit || content.trim().length < 10}
          className="btn-bouncy text-sm px-5 py-2"
        >
          {loading ? 'Sending…' : isEditing ? 'Save changes' : 'Submit feedback'}
        </button>

        {isEditing && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-bold rounded-xl transition-colors"
            style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

/**
 * FeedbackItem
 * Displays a single feedback entry with edit/delete actions for the author.
 */
export function FeedbackItem({ feedback, isAuthor, onEdit, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleEdit = async (newContent) => {
    setLoading(true);
    try {
      await onEdit(feedback.feedback_id, newContent);
      setIsEditing(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete(feedback.feedback_id);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    const d = new Date(Number(timestamp));
    return d.toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (isEditing) {
    return (
      <div className="rounded-xl p-4" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}>
        <FeedbackForm
          initialContent={feedback.content}
          isEditing
          onSubmit={handleEdit}
          onCancel={() => setIsEditing(false)}
          disabled={loading}
        />
      </div>
    );
  }

  return (
    <div className="card-paper p-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="font-extrabold text-sm break-words" style={{ color: 'var(--text-primary)' }}>{feedback.author_name}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-faint)' }}>{formatDate(feedback.created_at)}</p>
        </div>
        {isAuthor && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setIsEditing(true)}
              disabled={loading}
              className="p-1.5 rounded-lg transition-colors"
              style={{ background: 'var(--accent-dim)', color: 'var(--text-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
              title="Edit"
            >
              ✏️
            </button>
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={loading}
              className="p-1.5 rounded-lg transition-colors"
              style={{ background: 'var(--accent-dim)', color: 'var(--text-muted)' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-danger)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
              title="Delete"
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{feedback.content}</p>

      {/* Edited indicator */}
      {feedback.updated_at !== feedback.created_at && (
        <p className="text-xs mt-2 italic" style={{ color: 'var(--text-faint)' }}>
          Edited {formatDate(feedback.updated_at)}
        </p>
      )}

      {confirmOpen && (
        <ConfirmDialog
          message="Delete this comment?"
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => { setConfirmOpen(false); handleDelete(); }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
}
